import React from 'react';

import { PASSWORD_RULES } from '@rollops/app-shared/utils/passwordPolicy';

type PasswordRulesListProps = {
  /** Current password value used to live-evaluate which rules pass. */
  password: string;
  /** Optional id, useful when paired with `aria-describedby` on the input. */
  id?: string;
  /** Optional className appended to the wrapper. */
  className?: string;
};

/**
 * Live checklist that mirrors the project's Supabase password policy.
 *
 * Each rule is rendered with a check (✓) when satisfied and a bullet (•)
 * otherwise. Hidden screen-reader text announces "Requirement met" /
 * "Requirement not met" so the visual state is also conveyed via AT.
 *
 * Pair this with `validatePassword()` from
 * `@rollops/app-shared/utils/passwordPolicy` for the matching pre-submit
 * check, so the visual feedback and the validator can never disagree.
 */
export function PasswordRulesList({
  password,
  id,
  className,
}: PasswordRulesListProps) {
  return (
    <ul
      id={id}
      className={`mt-2 space-y-1 text-xs${className ? ` ${className}` : ''}`}
    >
      {PASSWORD_RULES.map((rule) => {
        const passed = rule.test(password);
        return (
          <li
            key={rule.id}
            className={passed ? 'text-emerald-400' : 'text-[#A9A3A3]'}
          >
            <span aria-hidden="true">{passed ? '✓' : '•'}</span>{' '}
            <span className="sr-only">
              {passed ? 'Requirement met:' : 'Requirement not met:'}
            </span>
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}
